import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/Input/Input';
import { SingleDatePicker } from '@/components/ui';
import type { OnboardingFormReturn } from '../useOnboardingForm';

export function CertificatesStep({ form }: { form: OnboardingFormReturn }) {
  const {
    certificates,
    certificateTitle,
    setCertificateTitle,
    certificateDate,
    setCertificateDate,
    pendingCertFile,
    certFileInputRef,
    handleCertFileChange,
    handleAddCertificate,
    handleRemoveCertificate,
  } = form;

  return (
    <Flex direction="column" gap="4">
      <Text fontSize="md" color="volyx.textSecondary">
        Add certificates you'd like organizations to see (PDF only, max 5MB). You can add more later.
      </Text>
      <Box display="grid" gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap="3">
        <Input
          id="onboarding-cert-title"
          name="onboarding-cert-title"
          autoComplete="off"
          value={certificateTitle}
          onChange={(e) => setCertificateTitle(e.target.value)}
          placeholder="Certificate title"
        />
        <SingleDatePicker
          popover
          showAge
          id="onboarding-cert-date"
          name="onboarding-cert-date"
          autoComplete="off"
          value={certificateDate}
          onChange={(date) => setCertificateDate(date ?? '')}
          maxDate={new Date()}
          placeholder="Select Date"
        />
        <input
          ref={certFileInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={handleCertFileChange}
        />
        <chakra.button
          type="button"
          px="6"
          py="3"
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          bg="volyx.lightGray"
          color="volyx.text"
          _hover={{ bg: 'volyx.mediumGray', transform: 'scale(1.01)' }}
          onClick={() => certFileInputRef.current?.click()}
        >
          {pendingCertFile ? 'Change PDF' : 'Select PDF'}
        </chakra.button>
        <chakra.button
          type="button"
          px="6"
          py="3"
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          bg="volyx.action"
          color="white"
          _hover={{ bg: 'rgba(0,122,255,0.9)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'translateY(-2px) scale(1.02)' }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          onClick={handleAddCertificate}
          disabled={!certificateTitle.trim() || !certificateDate || !pendingCertFile}
        >
          Add to list
        </chakra.button>
      </Box>
      {pendingCertFile ? (
        <Text fontSize="sm" color="volyx.textSecondary">Selected: {pendingCertFile.name}</Text>
      ) : null}

      <Flex direction="column" gap="2">
        {certificates.length === 0 ? (
          <Text color="volyx.textSecondary" fontSize="sm">No certificates queued yet.</Text>
        ) : (
          certificates.map((cert) => (
            <Flex
              key={cert.id}
              align="center"
              justify="space-between"
              gap="3"
              border="1px solid"
              borderColor="volyx.borderLight"
              borderRadius="lg"
              px="4"
              py="3"
            >
              <Box minW="0">
                <Text fontWeight="semibold" color="volyx.primary" lineClamp={1}>{cert.title}</Text>
                <Text fontSize="sm" color="volyx.textSecondary">{cert.date}</Text>
                <Text fontSize="xs" color="volyx.textSecondary" lineClamp={1}>{cert.file.name}</Text>
              </Box>
              <chakra.button
                bg="transparent"
                border="none"
                cursor="pointer"
                color="volyx.error"
                p="2"
                borderRadius="md"
                _hover={{ bg: 'rgba(255,59,48,0.1)' }}
                onClick={() => handleRemoveCertificate(cert.id)}
                aria-label={`Remove ${cert.title}`}
              >
                <X size={18} />
              </chakra.button>
            </Flex>
          ))
        )}
      </Flex>
    </Flex>
  );
}
